import type { Exercise } from '../../types.ts';
export const batch6Reason: Exercise[] = [
  {
    id: 'reason-shelf-swaps',
    category: 'reason',
    title: 'The shelf',
    topic: 'The shelf',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-shelf-swaps',
        prompt:
          'Eight labeled books should appear left to right in order 1,2,3,4,5,6,7,8. They currently appear as 3,1,2,5,4,7,6,8. One move swaps any two books, not necessarily adjacent. What is the minimum number of moves?',
        answer: {
          kind: 'number',
          value: 4,
          unit: 'swaps',
          tolerance: 0.0001,
        },
        explanation:
          'The minimum is four swaps, with both a matching cycle-count bound and an explicit sequence.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'The shelf — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A rearrangement can be understood by following where each item belongs. Start at a misplaced position, look at the item there, then go to its target position. Continue until the starting position returns. This forms a cycle. Different cycles use disjoint positions, so every permutation splits into cycles, including one-element cycles for already-correct items.',
            },
            {
              kind: 'paragraph',
              text: 'A cycle containing k items can be repaired in k−1 arbitrary swaps. Keep one position as a temporary anchor and repeatedly exchange its current item into that item’s target position. Each swap fixes at least one position, until the last two are fixed together. The construction explains sufficiency, but a minimum also requires a lower bound.',
            },
            {
              kind: 'paragraph',
              text: 'An arbitrary swap changes the number of permutation cycles by exactly one: it either merges two cycles or splits one into two. The fully sorted arrangement has one cycle per position. If an n-position arrangement begins with c cycles, reaching n cycles requires at least n−c swaps. Repairing each cycle separately uses exactly that many.',
            },
            {
              kind: 'equation',
              label: 'Minimum arbitrary swaps',
              lines: [
                {
                  left: 'sum over cycles of (length−1)',
                  right: 'n − number of cycles',
                  reason: 'Each swap can increase the cycle count by at most one.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Three misplaced books and one correct book',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'The order 2,3,1,4 has a three-cycle among positions 1,2,3 and a fixed position 4. Swap positions 1 and 3 to get 1,3,2,4, then positions 2 and 3 to sort it. Four positions minus two cycles gives two swaps.',
                },
                {
                  kind: 'table',
                  caption: 'Cycle repair',
                  headers: ['State', 'Swap'],
                  rows: [
                    ['2,3,1,4', 'Positions 1 and 3'],
                    ['1,3,2,4', 'Positions 2 and 3'],
                    ['1,2,3,4', 'Finished'],
                  ],
                },
              ],
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The daily cycles are positions (1,3,2), (4,5), (6,7), and the fixed position (8). There are four cycles among eight positions. The lower bound is therefore eight minus four, or four swaps.',
            },
            {
              kind: 'table',
              caption: 'An attaining sequence',
              headers: ['Swap positions', 'Resulting order'],
              rows: [
                ['1 and 2', '1,3,2,5,4,7,6,8'],
                ['2 and 3', '1,2,3,5,4,7,6,8'],
                ['4 and 5', '1,2,3,4,5,7,6,8'],
                ['6 and 7', '1,2,3,4,5,6,7,8'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Counting misplaced books and dividing by two is not generally valid. A swap can fix two books in a two-cycle, but a longer cycle cannot be repaired that efficiently throughout. Here seven books are misplaced and the rounded-up half happens to equal four, but the cycle proof is what establishes the answer for arbitrary arrangements.',
            },
            {
              kind: 'paragraph',
              text: 'The rule permits any pair to be swapped. Restricting swaps to neighboring books creates a different problem, whose cost depends on how many pairs appear in reversed order. Applying that adjacent-swap count here would overestimate the required moves.',
            },
            {
              kind: 'paragraph',
              text: 'The minimum is four swaps, with both a matching cycle-count bound and an explicit sequence.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'reason-pack-order',
    category: 'reason',
    title: 'The order',
    topic: 'The order',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-pack-order',
        prompt:
          'A shop sells an item only in packs of 1, 6 or 10. You need exactly 28 items, cannot split packs, and may buy any number of each size. What is the minimum number of packs?',
        answer: {
          kind: 'number',
          value: 4,
          unit: 'packs',
          tolerance: 0.0001,
        },
        explanation:
          'The minimum is four packs. Exhaustive enumeration of nonnegative counts of the three sizes independently confirms the bound and construction.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'The order — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A greedy choice selects what looks best immediately, such as the largest pack that fits the remaining quantity. Some pack systems make that rule optimal, but arbitrary sizes do not. The future remainder matters: using a large pack now may leave a quantity that requires many small packs.',
            },
            {
              kind: 'paragraph',
              text: 'One rigorous method defines F(n) as the minimum packs needed for exactly n items. Set F(0)=0. For each allowed size s no greater than n, any solution ending with that pack leaves an optimal subproblem of n−s items. Take the smallest value among 1+F(n−s). Considering every possible last pack makes the recurrence complete.',
            },
            {
              kind: 'equation',
              label: 'Optimal subproblem recurrence',
              lines: [
                {
                  left: 'F(n)',
                  right: '1 + minimum of F(n−s) over allowed s≤n',
                  reason:
                    'Every nonempty purchase has some last pack; choosing its type exhausts the possibilities.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A different pack system',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'With packs of 1, 3 and 4, a target of 6 can be bought as 3+3 using two packs. Greedy takes 4+1+1 and uses three. The last-pack comparison checks F(5)+1, F(3)+1 and F(2)+1 rather than committing to size four.',
                },
                {
                  kind: 'table',
                  caption: 'Small optimal values',
                  headers: ['Target', 'Minimum packs', 'One construction'],
                  rows: [
                    ['2', '2', '1+1'],
                    ['3', '1', '3'],
                    ['4', '1', '4'],
                    ['5', '2', '4+1'],
                    ['6', '2', '3+3'],
                  ],
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For a single small target, a direct impossibility bound can be shorter than a whole table. First exhibit a good construction. Then rule out every smaller number of packs using maximum size, possible sums or modular arithmetic. A construction alone proves only an upper bound; saying the largest packs look best is not an impossibility argument.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Four packs suffice: 10+6+6+6=28. Two packs cannot suffice because their maximum total is twenty. To rule out three, note that three tens total thirty. Replacing any ten by a six lowers the total by four; replacing one by a one lowers it by nine.',
            },
            {
              kind: 'equation',
              label: 'Three-pack impossibility',
              lines: [
                {
                  left: '30 − 28',
                  right: '2',
                  reason: 'No combination of reductions of four and nine can equal two.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'That argument includes every three-pack choice: start with three tens and independently replace each selected pack if needed. The desired reduction is positive but smaller than either possible replacement reduction. Thus neither three packs nor fewer can make the target.',
            },
            {
              kind: 'paragraph',
              text: 'Greedy would buy two tens, then a six, then two singles: five packs. The four-pack construction is better because it avoids the awkward remainder after the second ten. This is a concrete demonstration of why a locally maximal purchase can be globally wasteful.',
            },
            {
              kind: 'paragraph',
              text: 'The minimum is four packs. Exhaustive enumeration of nonnegative counts of the three sizes independently confirms the bound and construction.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'reason-party-guarantee',
    category: 'reason',
    title: 'At the gathering',
    topic: 'At the gathering',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-party-guarantee',
        prompt:
          'In a group, every pair of people either know each other or do not; knowing is mutual. What is the smallest group size that guarantees three people who all know one another OR three people none of whom know one another?',
        answer: {
          kind: 'number',
          value: 6,
          unit: 'people',
          tolerance: 0.0001,
        },
        explanation:
          'The smallest guaranteed size is six. The proof does not assume that knowing is transitive; it explicitly handles every relationship among the chosen three neighbors.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'At the gathering — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A relationship problem can be drawn as a complete graph: each person is a vertex and every pair is joined by an edge. Give an edge one of two labels, acquaintance or nonacquaintance. The desired triple is a triangle whose three edges have the same label. This translation replaces vague social intuition with a precise pairwise condition.',
            },
            {
              kind: 'paragraph',
              text: 'To prove a smallest guarantee, show two things: every arrangement at the claimed size succeeds, and at least one arrangement one smaller fails. A single successful gathering is not a guarantee. Conversely, a counterexample at a small size does not by itself show where success becomes unavoidable.',
            },
            {
              kind: 'example',
              title: 'A smaller pigeonhole fact',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'If one person is connected to three others, at least two of those three relationships have the same label. There are three edges and only two labels. They cannot each occupy a different label category. But this fact alone does not give a same-label triangle: the relationship between those two other people still has to be examined.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The pigeonhole principle says that placing more objects than categories forces a repeated category. A stronger version gives at least three edges of one label among five edges, because two labels with at most two edges each could cover only four. This is the local observation that makes the full six-person proof work.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction between a star and a triangle is crucial. Three people who each know a fourth person need not know each other. The proof must handle the relationships among the three selected neighbors instead of silently assuming friendship is transitive.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Choose one of six people, P. Among P’s five relationships, at least three share a label. Suppose P knows A, B and C. If any pair among A, B and C know each other, that pair together with P is an all-acquainted triple. If no pair does, A, B and C are an all-unacquainted triple. The argument is symmetric if P instead does not know the three.',
            },
            {
              kind: 'table',
              caption: 'The two possible outcomes after selecting three same-label neighbors',
              headers: ['Edges among the three', 'Result'],
              rows: [
                [
                  'At least one matches the edges to P',
                  'Its endpoints and P form a matching triangle',
                ],
                ['None matches the edges to P', 'All three mutual edges have the opposite label'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Five people provide the necessary counterexample. Place them around a pentagon. Let each person know exactly their two immediate neighbors and not know the other two. The acquaintance edges form a five-cycle, which has no triangle. The nonacquaintance edges form a five-point star, also a five-cycle, so they have no triangle either.',
            },
            {
              kind: 'paragraph',
              text: 'Every smaller group can be taken as a subset of that five-person counterexample, and removing people cannot create a new triangle. Thus no smaller size guarantees the condition. Exhaustively checking all two-label assignments on the ten edges for five vertices and fifteen edges for six vertices provides a separate finite verification.',
            },
            {
              kind: 'paragraph',
              text: 'The smallest guaranteed size is six. The proof does not assume that knowing is transitive; it explicitly handles every relationship among the chosen three neighbors.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'reason-beacon-times',
    category: 'reason',
    title: 'Two beacons',
    topic: 'Two beacons',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-beacon-times',
        prompt:
          'Starting at time 0, beacon A flashes at minutes 5,17,29,…, repeating every 12 minutes. Beacon B flashes at minutes 11,29,47,…, repeating every 18 minutes. At what earliest nonnegative minute do both flash together?',
        answer: {
          kind: 'number',
          value: 29,
          unit: 'minutes',
          tolerance: 0.0001,
        },
        explanation:
          'The first simultaneous flash is at minute 29; later simultaneous flashes occur at 65,101 and so on.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'Two beacons — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A periodic event is described by both its interval and its offset. Saying a beacon repeats every twelve minutes does not imply it flashes at time zero. To find coincidence, the same time must belong to both arithmetic progressions. Equal repetition intervals alone are not enough to compare starting phases.',
            },
            {
              kind: 'paragraph',
              text: 'Write A’s flash times as a+km and B’s as b+jn, where a and b are first times, m and n the periods, and k,j nonnegative integers. Equating them gives km−jn=b−a. A common divisor of m and n divides the left side, so it must divide the offset difference if any coincidence is possible.',
            },
            {
              kind: 'equation',
              label: 'Necessary compatibility',
              lines: [
                {
                  left: '(b − a) mod gcd(m, n)',
                  right: '0',
                  reason:
                    'The offset difference must leave zero remainder on division by the greatest common divisor of the periods.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A smaller phase mismatch',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A light flashes at 1,5,9,… and another at 3,9,15,… . Their periods are four and six, with greatest common divisor two. Their offsets differ by two, so coincidence is possible; listing the first light’s times finds nine. If the second instead began at two, the offset difference would be one, and coincidence would be impossible.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Once two periodic events coincide, the gap to their next coincidence must be a multiple of both periods. The least common multiple controls that repeat interval. It does not generally supply the first coincidence measured from the arbitrary time origin. Distinguishing first alignment from time between alignments prevents a common shortcut error.',
            },
            {
              kind: 'paragraph',
              text: 'For modest numbers, listing one sequence and testing membership in the other is already a rigorous algorithm. Test in increasing order, and the first success is earliest. For large numbers, integer equations can find the same alignment efficiently. Either method must respect nonnegative occurrence numbers rather than admitting a mathematically aligned time before either beacon started.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A’s nonnegative occurrences before and including 29 are 5,17,29. B’s are 11 and 29. Neither of A’s first two times belongs to B’s sequence, and 29 belongs to both. This gives an immediate exhaustive check of earliest coincidence.',
            },
            {
              kind: 'table',
              caption: 'Test A’s early flashes',
              headers: ['Time', 'Time minus B’s offset 11', 'Multiple of 18?'],
              rows: [
                ['5', '−6', 'No'],
                ['17', '6', 'No'],
                ['29', '18', 'Yes'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'The equation is 5+12k=11+18j, or 2k−3j=1 after dividing by six. The smallest allowed solution is k=2,j=1, giving time 29. Subsequent common flashes are 36 minutes apart because 36 is the least common multiple of 12 and 18.',
            },
            {
              kind: 'paragraph',
              text: 'Using 36 as the answer would confuse the repeat interval with the first shared occurrence. Using six, the greatest common divisor, would confuse compatibility of offsets with an actual flash time. Both quantities are useful, but neither substitutes for checking the offsets.',
            },
            {
              kind: 'paragraph',
              text: 'The first simultaneous flash is at minute 29; later simultaneous flashes occur at 65,101 and so on.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'reason-stack-flips',
    category: 'reason',
    title: 'The stack',
    topic: 'The stack',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-stack-flips',
        prompt:
          'Five numbered cards are stacked top to bottom as 3,1,5,2,4. One move reverses the order of the top k cards, where k may be any integer from 2 through 5. What is the minimum number of moves to put them in order 1,2,3,4,5 from top to bottom?',
        answer: {
          kind: 'number',
          value: 5,
          unit: 'moves',
          tolerance: 0.0001,
        },
        explanation:
          'The minimum is five moves: the five initial breaks require at least five, and the displayed sequence achieves five.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'The stack — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A prefix reversal flips a whole initial block. Internal neighbors in that block remain neighbors, just in the opposite order. This observation supplies a useful measure of disorder: count adjacent pairs whose numbers differ by more than one. Add an imaginary fixed card numbered n+1 beneath the stack so that the bottom boundary is included.',
            },
            {
              kind: 'paragraph',
              text: 'Call such a nonconsecutive adjacency a break. Reversing a prefix changes only one counted boundary: the edge between the reversed block and the part below it, or the sentinel if the whole stack flips. Every internal pair keeps the same absolute difference. Therefore one move can remove at most one break.',
            },
            {
              kind: 'equation',
              label: 'Lower bound from breaks',
              lines: [
                {
                  left: 'A lower bound on the move count',
                  right: 'Initial number of breaks',
                  reason:
                    'The target has zero breaks, and one prefix reversal can remove at most one.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Three-card example',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'For 2,1,3 with a fixed sentinel 4 below it, the pairs are (2,1),(1,3),(3,4). Only (1,3) is a break. Reversing the top two gives 1,2,3 and removes it, attaining the one-move lower bound.',
                },
                {
                  kind: 'table',
                  caption: 'Track the boundary',
                  headers: ['State including sentinel', 'Breaks'],
                  rows: [
                    ['2,1,3 | 4', '1'],
                    ['1,2,3 | 4', '0'],
                  ],
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A lower bound need not always be attainable. Some flips merely move a break or create a new one. To establish an optimum for a particular stack, exhibit a legal sequence reaching the target in exactly the bound’s number of moves. Breadth-first search over all permutations provides an independent way to find the shortest sequence for a small stack.',
            },
            {
              kind: 'paragraph',
              text: 'The sentinel is not an extra movable card. It records whether the bottom real card is consecutive with n+1, which matters when the entire stack reverses. Without that boundary the count can understate the work needed and a supposed proof can miss whole-stack flips.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Append sentinel 6 to the daily stack: 3,1,5,2,4 | 6. Every one of the five adjacent pairs has absolute difference greater than one. There are five breaks, so fewer than five prefix reversals cannot suffice.',
            },
            {
              kind: 'table',
              caption: 'A five-move construction',
              headers: ['Number of top cards reversed', 'Top-to-bottom result'],
              rows: [
                ['4', '2,5,1,3,4'],
                ['2', '5,2,1,3,4'],
                ['5', '4,3,1,2,5'],
                ['4', '2,1,3,4,5'],
                ['2', '1,2,3,4,5'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Each reversal obeys the permitted k range. Together they achieve the lower bound. A direct breadth-first search through the 120 possible permutations also reaches the target first at depth five, without relying on the break-count argument.',
            },
            {
              kind: 'paragraph',
              text: 'Counting inversions would be appropriate for adjacent swaps, but a prefix flip changes several pairwise orders at once. The chosen measure is useful precisely because it changes in a controlled way under this operation. An invariant or bound should be matched to the permitted moves, not borrowed from a superficially similar sorting problem.',
            },
            {
              kind: 'paragraph',
              text: 'The minimum is five moves: the five initial breaks require at least five, and the displayed sequence achieves five.',
            },
          ],
        },
      ],
    },
  },
];
